/**
 * Saidiseire's refusals: which sentence under `monitoring.error` a refused
 * request gets. Every code a monitoring route can answer with is listed here,
 * and `monitoring.test.ts` reads the route files to hold the list complete.
 *
 * React-free; re-exported by `monitoring.ts`.
 */
import { ApiError } from "./api";

/**
 * A refused request, as a key under `monitoring.error`.
 *
 * Read from the body's code first, because different refusals share a status:
 * 409 is "this client already watches that site", "the client is not active",
 * "monitoring is paused" and "the engine is switched off", and the operator
 * has to be told which.
 */
export type MonitorErrorKey =
  | "badUrl"
  | "duplicate"
  | "clientNotFound"
  | "clientInactive"
  | "tooSoon"
  | "busy"
  | "paused"
  | "engineOff"
  | "ourNetwork"
  | "notFound"
  | "badRecipients"
  | "badHeartbeat"
  | "badPlan"
  | "badPlatformSite"
  | "notInPlan"
  | "noKey"
  | "budgetReached"
  | "plansUnreadable"
  | "reportNotFound"
  | "reportBusy"
  | "notReady"
  | "badReportRecipient"
  | "mailboxUnavailable"
  | "sendFailed"
  | "alreadySent"
  | "notWatched"
  | "badMonth"
  | "forbidden"
  | "failed";

/** The codes the routes answer with (`sites.routes.ts`, `settings.routes.ts`,
 *  `keys.routes.ts`, the manual runs in `periodic/manual.ts`, and the speed,
 *  security and monthly report routes; docs/saidiseire-kiirus-turve-raport.md).
 *  `invalid_body`, `bad_project` and `check_failed` are left to "failed": the
 *  first two mean this client sent something its own form cannot produce, the
 *  third is a fault on the server's side, and none has a better sentence. */
const ERROR_BY_CODE: Record<string, MonitorErrorKey> = {
  bad_url: "badUrl",
  duplicate: "duplicate",
  client_not_found: "clientNotFound",
  client_inactive: "clientInactive",
  too_soon: "tooSoon",
  busy: "busy",
  paused: "paused",
  engine_off: "engineOff",
  our_network: "ourNetwork",
  not_found: "notFound",
  bad_recipient: "badRecipients",
  bad_heartbeat_url: "badHeartbeat",
  bad_plan: "badPlan",
  bad_platform_site: "badPlatformSite",
  not_in_plan: "notInPlan",
  no_key: "noKey",
  budget_reached: "budgetReached",
  plans_unreadable: "plansUnreadable",
  report_not_found: "reportNotFound",
  report_busy: "reportBusy",
  not_ready: "notReady",
  bad_report_recipient: "badReportRecipient",
  mailbox_unavailable: "mailboxUnavailable",
  send_failed: "sendFailed",
  already_sent: "alreadySent",
  not_watched: "notWatched",
  bad_month: "badMonth",
};

export function monitorErrorKey(err: unknown): MonitorErrorKey {
  if (!(err instanceof ApiError)) return "failed";
  const code = (err.body as { error?: unknown } | null)?.error;
  if (typeof code === "string" && code in ERROR_BY_CODE) return ERROR_BY_CODE[code]!;
  if (err.status === 403) return "forbidden";
  if (err.status === 404) return "notFound";
  if (err.status === 429) return "tooSoon";
  return "failed";
}
